// Matemática de conversión para curvas de crecimiento OMS/Fenton — pura, sin
// React/recharts, testeable con Vitest sin DOM (mismo criterio que
// paciente/curvaTermica.ts). Ver PLAN.md, Serie 028 / Riesgo 2:
//   - edad exacta en días entre nacimiento y medición.
//   - interpolación lineal de L/M/S entre los dos puntos que bracketan esa edad
//     (mismo método que las calculadoras oficiales de la OMS).
//   - conversión valor↔z-score con la fórmula LMS estándar.
// El eje de entrada no tiene por qué ser "día de vida" — cualquier dataset
// `[x, L, M, S][]` ordenado ascendente por x sirve (ej. semana gestacional
// para Fenton), por eso las funciones no se llaman "edad" salvo la que sí es
// específicamente eso.

export interface FilaLms {
  readonly x: number;
  readonly L: number;
  readonly M: number;
  readonly S: number;
}

/** Fila del dataset commiteado: tupla `[x, L, M, S]` (ver graficos/datos/*.json). */
export type FilaLmsTupla = readonly [number, number, number, number];

/** Un `import ... from '*.json'` tipa como `number[][]` genérico — este helper centraliza el cast a `FilaLmsTupla[]` en un solo lugar (el dato ya se valida por longitud de fila al generarlo, ver scripts/convertir-dataset-oms.mjs). */
export function comoFilasLms(json: readonly (readonly number[])[]): readonly FilaLmsTupla[] {
  return json as unknown as readonly FilaLmsTupla[];
}

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** Edad exacta en días (puede ser fraccionaria si se pasan horas) entre nacimiento y medición. */
export function edadExactaEnDias(fechaNacimientoIso: string, fechaMedicionIso: string): number {
  return (new Date(fechaMedicionIso).getTime() - new Date(fechaNacimientoIso).getTime()) / MS_POR_DIA;
}

function aFilaLms([x, L, M, S]: FilaLmsTupla): FilaLms {
  return { x, L, M, S };
}

/**
 * Interpolación lineal de L/M/S entre los dos puntos del dataset que
 * bracketan `x` — mismo método que las calculadoras oficiales de la OMS.
 * Si `x` cae fuera del rango del dataset, se usa el extremo más cercano (sin
 * extrapolar) — devuelve `undefined` solo si el dataset está vacío.
 */
export function interpolarLms(filas: readonly FilaLmsTupla[], x: number): FilaLms | undefined {
  if (filas.length === 0) return undefined;
  if (x <= filas[0][0]) return aFilaLms(filas[0]);
  if (x >= filas[filas.length - 1][0]) return aFilaLms(filas[filas.length - 1]);

  // Búsqueda binaria del primer índice cuyo x sea >= al buscado.
  let lo = 0;
  let hi = filas.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (filas[mid][0] < x) lo = mid + 1;
    else hi = mid;
  }
  const superior = filas[lo];
  const inferior = filas[lo - 1];
  if (!inferior || superior[0] === x) return aFilaLms(superior);

  const t = (x - inferior[0]) / (superior[0] - inferior[0]);
  return {
    x,
    L: inferior[1] + t * (superior[1] - inferior[1]),
    M: inferior[2] + t * (superior[2] - inferior[2]),
    S: inferior[3] + t * (superior[3] - inferior[3]),
  };
}

/** z-score de `valor` contra L/M/S — fórmula LMS estándar OMS (Cole 1990). */
export function zScore(valor: number, lms: Pick<FilaLms, 'L' | 'M' | 'S'>): number {
  const { L, M, S } = lms;
  if (L === 0) return Math.log(valor / M) / S;
  return (Math.pow(valor / M, L) - 1) / (L * S);
}

/** Inversa de `zScore` — el valor crudo correspondiente a un z-score dado, para dibujar las líneas SD como series. */
export function valorParaZ(lms: Pick<FilaLms, 'L' | 'M' | 'S'>, z: number): number {
  const { L, M, S } = lms;
  if (L === 0) return M * Math.exp(S * z);
  return M * Math.pow(1 + L * S * z, 1 / L);
}

export type PosicionMedicionTalla = 'acostado' | 'de-pie';

/** 24 meses en días — corte clínico estándar reusado en dos lugares: OMS longitud(acostado)/talla(de pie) y el tope de la edad corregida de un prematuro. */
const VEINTICUATRO_MESES_DIAS = 24 * 30.4375;

/**
 * La OMS resuelve L/M/S como una curva continua 0-5 años, pero el valor
 * MEDIDO depende de si se acostó o se paró al niño — mismatch de convención
 * cerca de los 24 meses (ver PLAN.md Riesgo 2). Se ajusta el valor crudo
 * ANTES de z-scorearlo: quien mide acostado a un niño de 2+ años obtiene ~0.7cm
 * de más que si lo hubiera medido de pie (y viceversa) — la OMS documenta este
 * ajuste como el estándar para no comparar contra la tabla equivocada.
 */
export function ajustarTallaPorPosicion(valorCm: number, edadDias: number, posicion: PosicionMedicionTalla): number {
  if (posicion === 'acostado' && edadDias >= VEINTICUATRO_MESES_DIAS) return valorCm - 0.7;
  if (posicion === 'de-pie' && edadDias < VEINTICUATRO_MESES_DIAS) return valorCm + 0.7;
  return valorCm;
}

const SEMANAS_GESTACION_TERMINO = 40;
const DIAS_POR_SEMANA = 7;

/**
 * Edad corregida (edad cronológica menos semanas de prematurez) — para
 * graficar a un prematuro contra las curvas OMS (0-5a) usando la edad que su
 * desarrollo realmente representa, no la edad desde el nacimiento. Se aplica
 * solo si nació antes de término (40 semanas) y solo hasta los 24 meses de
 * edad cronológica (criterio clínico estándar) — después de esa edad, o si
 * nació a término, devuelve la edad cronológica sin cambios. Ver PLAN.md
 * Riesgo 9.
 */
export function edadCorregida(edadCronologicaDias: number, edadGestacionalAlNacerSemanas: number): number {
  if (edadCronologicaDias >= VEINTICUATRO_MESES_DIAS) return edadCronologicaDias;
  const semanasPrematurez = SEMANAS_GESTACION_TERMINO - edadGestacionalAlNacerSemanas;
  if (semanasPrematurez <= 0) return edadCronologicaDias;
  return edadCronologicaDias - semanasPrematurez * DIAS_POR_SEMANA;
}
